import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";

// ─── IMAGE PATHS ─────────────────────────────────────────────────────────────
// All images go in /public/images/ — rename your files to match these names
const IMG = {
  logo:          "/images/logo.png",           // Your main logo file
  logoLight:     "/images/logo-light.png",     // White/cream version of logo
  heroBg:        "/images/hero-bg.jpeg",        // Billboard wall photo     (WA0029)
  cattleHero:    "/images/cattle-hero.jpeg",    // Cows feeding photo       (WA0028)
  beefProducts:  "/images/beef-products.jpeg",  // 4 beef packs lineup      (WA0003)
  beefLifestyle: "/images/beef-lifestyle.webp", // Family kitchen photo     (WA0023)
  freshlockBag:  "/images/freshlock-bag.jpeg",  // Single green bag         (WA0013)
  farmFlyer:     "/images/farm-flyer.jpeg",     // Farm Smart Eat Clean     (WA0008)
  billboards:    "/images/billboards.jpeg",     // 3-billboard mockup       (WA0002)
  wallAds:       "/images/wall-ads.jpeg",       // Long wall of posters     (WA0016)
};

// ─── EMAILJS CREDENTIALS ─────────────────────────────────────────────────────
// Replace with your real values from emailjs.com (see SETUP-GUIDE.txt)
const EMAILJS_SERVICE_ID  = "service_94mkpfr";
const EMAILJS_TEMPLATE_ID = "template_b0zyng9";
const EMAILJS_PUBLIC_KEY  = "LIQghJAqL6Mjcdlyw";

// ─── WHATSAPP ─────────────────────────────────────────────────────────────────
const WA = `https://wa.me/2348130841879?text=Hello%20Doxapearl%2C%20I%27m%20interested%20in%20your%20services.`;

// ─── STYLES ──────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  :root{
    --forest:#0C3320;--green:#1a5c30;--mid:#2d7a47;--light:#5DCA91;
    --cream:#F2EDE0;--cdark:#E8E0CE;--gold:#D4A017;--goldl:#F0C040;
    --dark:#0a1f10;--serif:'Playfair Display',Georgia,serif;
    --sans:'DM Sans',sans-serif;--r:12px;--rl:20px;
  }
  html{scroll-behavior:smooth}
  body{font-family:var(--sans);background:var(--cream);color:var(--forest);overflow-x:hidden}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:var(--forest)}
  ::-webkit-scrollbar-thumb{background:var(--gold);border-radius:3px}

  /* NAV */
  .nav{
    position:fixed;top:0;left:0;right:0;z-index:1000;height:80px;
    display:flex;align-items:center;justify-content:space-between;padding:0 5%;
    background:rgba(12,51,32,0.97);backdrop-filter:blur(16px);
    border-bottom:1px solid rgba(255,255,255,0.06);
  }
  .nav-logo{display:flex;align-items:center;gap:10px;text-decoration:none}
  .nav-logo-img{height:56px;width:auto;object-fit:contain;max-width:180px}
  .nav-logo-fb{font-family:var(--serif);font-size:22px;font-weight:700;color:var(--cream);line-height:1.1}
  .nav-logo-fb span{font-size:11px;font-weight:400;display:block;color:var(--light);letter-spacing:1.5px}
  .nav-links{display:flex;align-items:center;gap:28px}
  .nav-link{color:rgba(242,237,224,0.7);text-decoration:none;font-size:14px;transition:color .2s}
  .nav-link:hover{color:var(--goldl)}
  .nav-cta{background:var(--gold);color:var(--forest);padding:9px 22px;border-radius:99px;font-size:14px;font-weight:600;text-decoration:none;transition:all .2s}
  .nav-cta:hover{background:var(--goldl);transform:translateY(-1px);box-shadow:0 4px 16px rgba(212,160,23,.35)}
  .hbg{display:none;flex-direction:column;gap:5px;cursor:pointer;padding:4px;background:none;border:none}
  .hbg span{width:24px;height:2px;background:var(--cream);border-radius:2px;transition:all .3s;display:block}
  .mob-menu{
    position:fixed;top:80px;left:0;right:0;z-index:999;
    background:rgba(12,51,32,0.98);backdrop-filter:blur(16px);
    padding:28px 5%;display:flex;flex-direction:column;gap:22px;
    transform:translateY(-110%);transition:transform .35s cubic-bezier(.4,0,.2,1);
    border-bottom:1px solid rgba(255,255,255,0.06);
  }
  .mob-menu.open{transform:translateY(0)}

  /* HERO */
  .hero{
    min-height:100vh;display:flex;flex-direction:column;justify-content:center;
    padding:128px 5% 80px;position:relative;overflow:hidden;
  }
  .hero-bg{
    position:absolute;inset:0;z-index:0;
    background-image:url('/images/hero-bg.jpg');
    background-size:cover;background-position:center;
    filter:brightness(.22) saturate(.7);
  }
  .hero-ov{
    position:absolute;inset:0;z-index:1;
    background:linear-gradient(135deg,rgba(12,51,32,.88) 0%,rgba(10,31,16,.55) 60%,transparent 100%);
  }
  .hero-grid{
    position:absolute;inset:0;z-index:1;opacity:.04;
    background-image:linear-gradient(rgba(242,237,224,1) 1px,transparent 1px),
                     linear-gradient(90deg,rgba(242,237,224,1) 1px,transparent 1px);
    background-size:60px 60px;
  }
  .hero-body{position:relative;z-index:2;max-width:820px}
  .badge{
    display:inline-flex;align-items:center;gap:8px;
    background:rgba(212,160,23,.15);border:1px solid rgba(212,160,23,.4);
    color:var(--goldl);font-size:11px;font-weight:600;letter-spacing:2px;
    padding:7px 18px;border-radius:99px;margin-bottom:28px;text-transform:uppercase;
  }
  .badge-dot{width:6px;height:6px;border-radius:50%;background:var(--goldl);animation:pulse 2s infinite}
  @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(1.4)}}
  .hero-h1{font-family:var(--serif);font-size:clamp(44px,7vw,90px);font-weight:900;color:var(--cream);line-height:1}
  .hero-gold{color:var(--gold);display:block}
  .hero-sub{font-size:clamp(15px,2vw,18px);color:rgba(242,237,224,.65);font-weight:300;margin:22px 0 40px;max-width:540px;line-height:1.75}
  .hero-btns{display:flex;gap:16px;flex-wrap:wrap}
  .btn-p{background:var(--gold);color:var(--forest);padding:14px 32px;border-radius:99px;font-size:15px;font-weight:600;text-decoration:none;transition:all .25s;border:none;cursor:pointer;display:inline-block;font-family:var(--sans)}
  .btn-p:hover{background:var(--goldl);transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,160,23,.3)}
  .btn-p:disabled{opacity:.6;cursor:not-allowed;transform:none}
  .btn-g{background:transparent;color:var(--cream);padding:14px 32px;border-radius:99px;font-size:15px;font-weight:500;text-decoration:none;border:1.5px solid rgba(242,237,224,.3);transition:all .2s;display:inline-block}
  .btn-g:hover{border-color:var(--cream);background:rgba(242,237,224,.08)}
  .hero-stats{display:flex;gap:40px;margin-top:60px;padding-top:36px;border-top:1px solid rgba(242,237,224,.1);flex-wrap:wrap}
  .stat-n{font-family:var(--serif);font-size:30px;font-weight:700;color:var(--gold)}
  .stat-l{font-size:11px;color:rgba(242,237,224,.45);margin-top:2px;letter-spacing:.5px}
  .scroll-cue{position:absolute;bottom:32px;left:50%;transform:translateX(-50%);z-index:2;display:flex;flex-direction:column;align-items:center;gap:8px;color:rgba(242,237,224,.3);font-size:10px;letter-spacing:2px;text-transform:uppercase;animation:sb 2s infinite}
  .scroll-line{width:1px;height:36px;background:rgba(242,237,224,.2)}
  @keyframes sb{0%,100%{transform:translateX(-50%) translateY(0)}50%{transform:translateX(-50%) translateY(7px)}}

  /* SECTIONS */
  .sec{padding:100px 5%}
  .lbl{font-size:11px;font-weight:600;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:12px;display:block}
  .h2{font-family:var(--serif);font-size:clamp(30px,4vw,52px);font-weight:700;color:var(--forest);line-height:1.15;margin-bottom:16px}
  .h2l{color:var(--cream)}
  .sub{font-size:16px;color:#4a6a55;line-height:1.8;max-width:560px}
  .subl{color:rgba(242,237,224,.6)}

  /* ABOUT */
  .about-grid{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center;margin-top:56px}
  .vm-pair{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:28px}
  .vm{background:var(--forest);padding:24px;border-radius:var(--rl)}
  .vm-icon{font-size:26px;margin-bottom:10px}
  .vm-t{font-family:var(--serif);font-size:17px;font-weight:700;color:var(--gold);margin-bottom:8px}
  .vm-b{font-size:12.5px;color:rgba(242,237,224,.6);line-height:1.7}
  .vtags{display:flex;gap:10px;flex-wrap:wrap;margin-top:20px}
  .vtag{background:var(--forest);color:var(--cream);font-size:12px;padding:5px 14px;border-radius:99px;font-weight:500}
  /* photo stack */
  .pstack{position:relative;height:480px}
  .pmain{position:absolute;top:0;right:0;width:88%;height:380px;border-radius:var(--rl);object-fit:cover;box-shadow:0 8px 32px rgba(12,51,32,.15)}
  .paccent{position:absolute;bottom:0;left:0;width:60%;height:240px;border-radius:var(--rl);object-fit:cover;border:4px solid var(--cream);box-shadow:0 8px 32px rgba(12,51,32,.15)}
  .pbadge{position:absolute;top:20px;left:10px;z-index:2;background:var(--gold);color:var(--forest);font-size:11px;font-weight:600;padding:6px 14px;border-radius:99px;letter-spacing:1px}
  /* placeholder */
  .ph{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;background:linear-gradient(135deg,#1a5c30,#0C3320);color:rgba(242,237,224,.4);font-size:12px;font-family:var(--sans);text-align:center}
  .ph-icon{font-size:36px}

  /* DIVISIONS */
  .divs-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:20px;margin-top:56px}
  .dc{background:rgba(242,237,224,.05);border:1px solid rgba(242,237,224,.1);border-radius:var(--rl);padding:32px;transition:all .3s;position:relative;overflow:hidden}
  .dc::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(45,122,71,.15) 0%,transparent 70%);opacity:0;transition:opacity .3s}
  .dc:hover{transform:translateY(-5px);border-color:rgba(212,160,23,.4)}
  .dc:hover::after{opacity:1}
  .dc-icon{width:50px;height:50px;border-radius:14px;background:rgba(212,160,23,.15);border:1px solid rgba(212,160,23,.3);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:18px}
  .dc-name{font-family:var(--serif);font-size:21px;font-weight:700;color:var(--cream);margin-bottom:6px}
  .dc-tag{font-size:11px;font-weight:600;letter-spacing:1.5px;color:var(--gold);text-transform:uppercase;margin-bottom:12px}
  .dc-desc{font-size:13.5px;color:rgba(242,237,224,.55);line-height:1.7}
  .dc-feats{margin-top:18px;display:flex;flex-direction:column;gap:7px}
  .dc-feat{display:flex;align-items:flex-start;gap:8px;font-size:13px;color:rgba(242,237,224,.6)}
  .dc-dot{width:5px;height:5px;border-radius:50%;background:var(--light);flex-shrink:0;margin-top:7px}

  /* PHOTO BANNER */
  .pban{position:relative;overflow:hidden}
  .pban-img{width:100%;height:320px;object-fit:cover;object-position:center;display:block}
  .pban::after{content:'';position:absolute;inset:0;background:linear-gradient(to right,rgba(12,51,32,.82) 0%,rgba(12,51,32,.2) 60%,transparent 100%)}
  .pban-txt{position:absolute;top:50%;left:5%;transform:translateY(-50%);z-index:2}
  .pban-txt h2{font-family:var(--serif);font-size:clamp(22px,4vw,46px);font-weight:700;color:var(--cream)}
  .pban-txt p{font-size:15px;color:rgba(242,237,224,.7);margin-top:8px}

  /* AGRO ESTATE */
  .crops{display:grid;grid-template-columns:repeat(auto-fit,minmax(185px,1fr));gap:16px;margin-top:44px}
  .cc{background:var(--forest);border-radius:var(--rl);padding:26px 18px;text-align:center;transition:all .3s;cursor:pointer}
  .cc:hover{transform:translateY(-5px);background:var(--green)}
  .cc-e{font-size:38px;margin-bottom:10px}
  .cc-n{font-family:var(--serif);font-size:17px;font-weight:700;color:var(--cream);margin-bottom:4px}
  .cc-y{font-size:11px;color:var(--gold);font-weight:500}
  .steps{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:20px;margin-top:44px}
  .step{text-align:center;padding:22px 14px}
  .step-n{width:46px;height:46px;border-radius:50%;background:var(--forest);color:var(--gold);font-family:var(--serif);font-size:18px;font-weight:700;display:flex;align-items:center;justify-content:center;margin:0 auto 14px}
  .step-t{font-size:15px;font-weight:600;color:var(--forest);margin-bottom:6px}
  .step-d{font-size:13px;color:#5a7060;line-height:1.65}

  /* CATTLE */
  .cattle-grid{display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:start;margin-top:52px}
  .c-img-wrap{position:relative;border-radius:var(--rl);overflow:hidden}
  .c-img{width:100%;height:380px;object-fit:cover;display:block}
  .c-cap{position:absolute;bottom:0;left:0;right:0;padding:20px;background:linear-gradient(to top,rgba(12,51,32,.9) 0%,transparent 100%)}
  .c-cap strong{color:var(--gold);display:block;font-size:16px;font-family:var(--serif);margin-bottom:2px}
  .c-cap p{font-size:13px;color:rgba(242,237,224,.7)}
  .cattle-plans{display:flex;flex-direction:column;gap:14px;margin-bottom:24px}
  .cp{background:rgba(242,237,224,.06);border:1px solid rgba(242,237,224,.1);border-radius:var(--rl);padding:22px;transition:all .25s}
  .cp:hover{border-color:rgba(212,160,23,.4)}
  .cp-t{font-family:var(--serif);font-size:17px;font-weight:700;color:var(--cream);margin-bottom:3px}
  .cp-s{font-size:11px;color:var(--gold);font-weight:600;margin-bottom:8px}
  .cp-d{font-size:13px;color:rgba(242,237,224,.55);line-height:1.65}
  .del-t{font-size:14px;font-weight:600;color:var(--cream);margin-bottom:12px}
  .del{display:flex;align-items:flex-start;gap:12px;background:rgba(242,237,224,.05);border-radius:var(--r);padding:14px;margin-bottom:10px}
  .del-icon{font-size:20px;flex-shrink:0}
  .del-n{font-size:13px;font-weight:600;color:var(--cream);margin-bottom:2px}
  .del-d{font-size:12px;color:rgba(242,237,224,.5)}

  /* BEEF PRODUCTS */
  .beef-sec{background:var(--dark);padding:80px 5%}
  .beef-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:48px;align-items:center}
  .beef-main-img{width:100%;border-radius:var(--rl);object-fit:cover;height:380px}
  .beef-items{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .bi{background:rgba(242,237,224,.06);border:1px solid rgba(242,237,224,.1);border-radius:var(--r);padding:16px 14px;text-align:center;transition:all .2s}
  .bi:hover{background:var(--green)}
  .bi-i{font-size:22px;margin-bottom:6px}
  .bi-n{font-size:13px;font-weight:600;color:var(--cream);margin-bottom:2px}
  .bi-s{font-size:11px;color:rgba(242,237,224,.45)}

  /* CATALYST */
  .cat-sec{background:var(--dark);position:relative;overflow:hidden}
  .cat-glow{position:absolute;top:-120px;right:-80px;width:480px;height:480px;border-radius:50%;background:radial-gradient(circle,rgba(45,122,71,.18) 0%,transparent 70%);pointer-events:none}
  .cat-grid{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center;margin-top:56px}
  .tok{background:linear-gradient(135deg,rgba(26,92,48,.4) 0%,rgba(12,51,32,.85) 100%);border:1px solid rgba(93,202,145,.2);border-radius:var(--rl);padding:36px;position:relative;overflow:hidden}
  .tok::before{content:'';position:absolute;top:-40px;right:-40px;width:140px;height:140px;border-radius:50%;background:radial-gradient(circle,rgba(212,160,23,.12) 0%,transparent 70%)}
  .tok-logo{width:68px;height:68px;border-radius:50%;background:linear-gradient(135deg,#1a5c30,#2d7a47);border:2px solid rgba(212,160,23,.4);display:flex;align-items:center;justify-content:center;font-size:30px;margin-bottom:18px}
  .tok-n{font-family:var(--serif);font-size:26px;font-weight:700;color:var(--cream);margin-bottom:3px}
  .tok-t{font-size:13px;color:var(--light);font-weight:500;margin-bottom:14px}
  .tok-d{font-size:13.5px;color:rgba(242,237,224,.6);line-height:1.7;margin-bottom:20px}
  .tok-q{font-style:italic;font-size:13px;color:var(--gold);padding-left:12px;border-left:2px solid var(--gold);margin-bottom:22px}
  .tok-stats{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .ts{background:rgba(242,237,224,.05);border-radius:var(--r);padding:14px}
  .ts-v{font-family:var(--serif);font-size:18px;font-weight:700;color:var(--gold)}
  .ts-l{font-size:11px;color:rgba(242,237,224,.4);margin-top:2px}
  .cat-feats{display:flex;flex-direction:column;gap:14px}
  .cf{display:flex;gap:14px;padding:18px;border-radius:var(--r);background:rgba(242,237,224,.04);border:1px solid rgba(242,237,224,.08);transition:all .25s}
  .cf:hover{background:rgba(242,237,224,.07);border-color:rgba(212,160,23,.2)}
  .cf-i{font-size:20px;width:40px;height:40px;display:flex;align-items:center;justify-content:center;background:rgba(45,122,71,.3);border-radius:10px;flex-shrink:0}
  .cf-t{font-size:14px;font-weight:600;color:var(--cream);margin-bottom:3px}
  .cf-d{font-size:12.5px;color:rgba(242,237,224,.5);line-height:1.6}

  /* CONTACT */
  .con-grid{display:grid;grid-template-columns:1fr 1fr;gap:72px;margin-top:56px;align-items:start}
  .ci-list{display:flex;flex-direction:column;gap:18px;margin-bottom:24px}
  .ci{display:flex;align-items:flex-start;gap:14px}
  .ci-icon{width:42px;height:42px;border-radius:12px;flex-shrink:0;background:var(--forest);display:flex;align-items:center;justify-content:center;font-size:17px}
  .ci-lbl{font-size:11px;color:#6a8070;font-weight:500;margin-bottom:2px}
  .ci-val{font-size:14px;font-weight:600;color:var(--forest)}
  .map-frame{width:100%;height:200px;border-radius:var(--rl);border:none;margin-top:8px}
  .socials{display:flex;gap:10px;flex-wrap:wrap;margin-top:20px}
  .sc{background:var(--forest);color:var(--cream);padding:7px 16px;border-radius:99px;font-size:12px;font-weight:500;text-decoration:none;border:1px solid rgba(242,237,224,.08);transition:all .2s}
  .sc:hover{background:var(--green);color:var(--gold)}
  /* form */
  .form-wrap{background:var(--forest);border-radius:var(--rl);padding:36px}
  .form-title{font-family:var(--serif);font-size:22px;font-weight:700;color:var(--cream);margin-bottom:22px}
  .form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .fg{margin-bottom:16px}
  .fl{display:block;font-size:11px;font-weight:500;color:rgba(242,237,224,.55);margin-bottom:5px;letter-spacing:.5px;text-transform:uppercase}
  .fi{width:100%;background:rgba(242,237,224,.08);border:1px solid rgba(242,237,224,.14);border-radius:var(--r);padding:12px 14px;font-size:14px;font-family:var(--sans);color:var(--cream);outline:none;transition:border-color .2s}
  .fi:focus{border-color:rgba(212,160,23,.5);background:rgba(242,237,224,.1)}
  .fi::placeholder{color:rgba(242,237,224,.25)}
  select.fi{appearance:none;cursor:pointer}
  textarea.fi{resize:vertical;min-height:90px}
  .f-success{background:rgba(93,202,145,.12);border:1px solid rgba(93,202,145,.3);border-radius:var(--rl);padding:32px;text-align:center}
  .f-err{background:rgba(220,80,80,.1);border:1px solid rgba(220,80,80,.3);border-radius:var(--r);padding:12px 16px;font-size:13px;color:#ff9090;margin-bottom:16px}

  /* WHATSAPP */
  .wa{
    position:fixed;bottom:28px;right:28px;z-index:999;
    width:58px;height:58px;border-radius:50%;
    background:#25D366;color:white;
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 4px 20px rgba(37,211,102,.45);
    text-decoration:none;transition:all .25s;
  }
  .wa:hover{transform:scale(1.1);box-shadow:0 6px 28px rgba(37,211,102,.6)}
  .wa svg{width:28px;height:28px;fill:white}
  .wa-tip{
    position:absolute;right:68px;background:var(--forest);color:var(--cream);
    font-size:13px;font-weight:500;white-space:nowrap;
    padding:7px 14px;border-radius:99px;font-family:var(--sans);
    opacity:0;transform:translateX(8px);transition:all .2s;pointer-events:none;
  }
  .wa:hover .wa-tip{opacity:1;transform:translateX(0)}
  .wa-ring{position:absolute;inset:-4px;border-radius:50%;background:rgba(37,211,102,.3);animation:ring 2s infinite}
  @keyframes ring{0%{transform:scale(1);opacity:.6}100%{transform:scale(1.55);opacity:0}}

  /* FOOTER */
  .footer{background:var(--dark);padding:64px 5% 28px;border-top:1px solid rgba(242,237,224,.05)}
  .fg-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:44px;margin-bottom:44px}
  .fc-t{font-size:11px;font-weight:600;letter-spacing:1.5px;color:var(--gold);text-transform:uppercase;margin-bottom:16px}
  .fc-n{font-family:var(--serif);font-size:20px;font-weight:700;color:var(--cream);margin-bottom:8px}
  .fc-s{font-size:13px;color:rgba(242,237,224,.38);line-height:1.65;max-width:230px}
  .fl-link{display:block;font-size:13px;color:rgba(242,237,224,.45);text-decoration:none;margin-bottom:10px;transition:color .2s}
  .fl-link:hover{color:var(--cream)}
  .f-bottom{border-top:1px solid rgba(242,237,224,.05);padding-top:22px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px}
  .f-copy{font-size:12px;color:rgba(242,237,224,.28)}
  .f-tag{font-size:12px;color:var(--gold);font-style:italic}

  /* RESPONSIVE */
  @media(max-width:900px){
    .nav-links{display:none}
    .hbg{display:flex}
    .about-grid,.cattle-grid,.cat-grid,.con-grid,.beef-grid{grid-template-columns:1fr;gap:36px}
    .fg-grid{grid-template-columns:1fr 1fr}
  }
  @media(max-width:600px){
    .vm-pair,.tok-stats,.beef-items,.form-row{grid-template-columns:1fr}
    .fg-grid{grid-template-columns:1fr}
    .hero-stats{gap:22px}
    .pstack{height:320px}
    .pmain{height:260px}
    .paccent{height:160px}
  }
`;

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const Logo = ({ light = false, h = 40 }) => {
  const [err, setErr] = useState(false);
  const src = light ? IMG.logoLight : IMG.logo;
  if (!err) return <img src={src} alt="Doxapearl Group" style={{ height: h, width: "auto", objectFit: "contain" }} onError={() => setErr(true)} />;
  return <div className="nav-logo-fb" style={{ color: light ? "#F2EDE0" : "#0C3320" }}>Doxapearl<span>GROUP</span></div>;
};

// ─── SMART IMG ────────────────────────────────────────────────────────────────
const Photo = ({ src, alt, className, style: s, icon = "📸" }) => {
  const [err, setErr] = useState(false);
  if (!err) return <img src={src} alt={alt} className={className} style={s} onError={() => setErr(true)} />;
  return (
    <div className={`ph ${className || ""}`} style={s}>
      <span className="ph-icon">{icon}</span>
      <span>Add photo:<br />/public/images/{src.split("/").pop()}</span>
    </div>
  );
};

// ─── APP ─────────────────────────────────────────────────────────────────────
export default function DoxapearlWebsite() {
  const [open, setOpen]       = useState(false);
  const [fd, setFd]           = useState({ name:"", email:"", phone:"", interest:"", message:"" });
  const [fstate, setFstate]   = useState("idle");
  const [ferr, setFerr]       = useState("");
  const formRef               = useRef();

  const send = async (e) => {
    e.preventDefault();
    setFstate("sending"); setFerr("");
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY);
      setFstate("success");
      setFd({ name:"", email:"", phone:"", interest:"", message:"" });
    } catch {
      setFstate("error");
      setFerr("Message could not be sent. Please WhatsApp or call us directly.");
    }
  };

  const NAVLINKS = ["About","Divisions","Agro Estate","Cattle & Beef","$CLYST","Contact"];
  const navHref  = l => `#${l.toLowerCase().replace(/ & /g,"-").replace(/ /g,"-").replace("$","")}`;

  return (
    <>
      <style>{css}</style>

      {/* NAV */}
      <nav className="nav">
        <a href="#home" className="nav-logo"><Logo light h={90} /></a>
        <div className="nav-links">
          {NAVLINKS.map(l => <a key={l} href={navHref(l)} className="nav-link">{l}</a>)}
          <a href="#contact" className="nav-cta">Invest Now</a>
        </div>
        <button className="hbg" onClick={() => setOpen(!open)} aria-label="Menu">
          <span style={{ transform: open ? "rotate(45deg) translate(5px,5px)" : "none" }} />
          <span style={{ opacity: open ? 0 : 1 }} />
          <span style={{ transform: open ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
        </button>
      </nav>
      <div className={`mob-menu ${open ? "open" : ""}`}>
        {NAVLINKS.map(l => <a key={l} href={navHref(l)} className="nav-link" onClick={() => setOpen(false)}>{l}</a>)}
        <a href="#contact" className="btn-p" onClick={() => setOpen(false)} style={{ textAlign:"center" }}>Invest Now</a>
      </div>

      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-bg" />
        <div className="hero-ov" />
        <div className="hero-grid" />
        <div className="hero-body">
          <div className="badge"><span className="badge-dot" />Nigeria's Agro-Wealth Ecosystem</div>
          <h1 className="hero-h1">Grow Wealth.<span className="hero-gold">Feed the Future.</span></h1>
          <p className="hero-sub">Doxapearl Group bridges agriculture, real estate, and decentralised finance — turning nature's abundance into lasting wealth for everyday Nigerians and global investors alike.</p>
          <div className="hero-btns">
            <a href="#divisions" className="btn-p">Explore Opportunities</a>
            <a href="#contact" className="btn-g">Talk to Us</a>
          </div>
          <div className="hero-stats">
            {[{ n:"4+", l:"Business Divisions" },{ n:"3", l:"Agro Crop Types" },{ n:"BSC", l:"Blockchain" },{ n:"Abuja", l:"HQ & Beef Hub" }].map(s => (
              <div key={s.l}><div className="stat-n">{s.n}</div><div className="stat-l">{s.l}</div></div>
            ))}
          </div>
        </div>
        <div className="scroll-cue"><div className="scroll-line" />Scroll</div>
      </section>

      {/* ABOUT */}
      <section className="sec" style={{ background:"var(--cream)" }} id="about">
        <span className="lbl">Who We Are</span>
        <h2 className="h2">One Group.<br />Many Pathways to Wealth.</h2>
        <div className="about-grid">
          <div>
            <p className="sub">Doxapearl Group is a Nigerian agro-investment conglomerate operating across agriculture, agro-real estate, premium livestock, and blockchain finance — delivering nature's best and creating sustainable wealth.</p>
            <div className="vm-pair">
              <div className="vm"><div className="vm-icon">🌍</div><div className="vm-t">Vision</div><div className="vm-b">To become a global leading force in Agriculture, Agro-Estate, Real Estate, and the Agro-Byproducts industry, promoting Healthy living while creating sustainable Wealth.</div></div>
              <div className="vm"><div className="vm-icon">🎯</div><div className="vm-t">Mission</div><div className="vm-b">To create Healthier lives and Wealthier Homes through innovative agricultural practices, providing Agro-Wealth Solutions and Smart Homes built on integrity.</div></div>
            </div>
            <div className="vtags">
              {["Integrity","Innovation","Accessibility","Sustainability","Excellence"].map(v => <span key={v} className="vtag">{v}</span>)}
            </div>
          </div>
          <div className="pstack">
            <span className="pbadge">Est. Abuja, Nigeria</span>
            <Photo src={IMG.billboards} alt="Doxapearl brand billboards" className="pmain" icon="🏙️" />
            <Photo src={IMG.farmFlyer}  alt="Farm Smart Eat Clean flyer"  className="paccent" icon="🌿" />
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="sec" style={{ background:"var(--forest)" }} id="divisions">
        <span className="lbl">Our Divisions</span>
        <h2 className="h2 h2l">The Doxapearl Ecosystem</h2>
        <p className="sub subl" style={{ marginTop:8 }}>Four distinct business units working together to deliver wealth, food, and financial freedom.</p>
        <div className="divs-grid">
          {[
            { i:"🌿", n:"Agro Estate", t:"Land Investment", d:"Buy a plot of land and have palm, cocoa, or plantain planted and managed for you — earning farm income for life.", f:["One-time land purchase","Managed by our agri-experts","Annual harvest income","Appreciating land asset"] },
            { i:"🐄", n:"Cattle & Beef", t:"Livestock & Meat", d:"Invest in a cow, let us raise it, and earn your profit after 12 months. Plus premium frozen and dried beef nationwide.", f:["Cow ownership investment","12-month return cycle","Freshlock premium beef","Nationwide dried meat"] },
            { i:"⚗", n:"Catalyst $CLYST", t:"DeFi · BSC", d:"The native utility token of the Agrovest ecosystem — powering payments, governance, and access across all Doxapearl digital products.", f:["BEP-20 on BSC","240M total supply","3% buy/sell tax","Farmvest · AI · Wallet"] },
            { i:"🏠", n:"Castle & Properties", t:"Real Estate", d:"Smart homes and agro-integrated real estate solutions — where your home and your farm investment grow together.", f:["Agro-integrated homes","Smart property solutions","Accessible & affordable","Built on integrity"] },
          ].map(d => (
            <div key={d.n} className="dc">
              <div className="dc-icon">{d.i}</div>
              <div className="dc-name">{d.n}</div>
              <div className="dc-tag">{d.t}</div>
              <div className="dc-desc">{d.d}</div>
              <div className="dc-feats">{d.f.map(f => <div key={f} className="dc-feat"><div className="dc-dot" />{f}</div>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTO BANNER */}
      <div className="pban">
        <Photo src={IMG.wallAds} alt="Doxapearl outdoor advertising" className="pban-img" icon="📸" />
        <div className="pban-txt"><h2>Real Estate. Agriculture.</h2><p>Two pillars. One mission. Endless wealth.</p></div>
      </div>

      {/* AGRO ESTATE */}
      <section className="sec" style={{ background:"var(--cdark)" }} id="agro-estate">
        <span className="lbl">Agro Estate</span>
        <h2 className="h2">Own Land. Plant Wealth.<br />Earn for Life.</h2>
        <p className="sub">Buy a plot, choose your crop, and our expert team handles everything — planting, farming, harvesting — while you earn income season after season.</p>
        <div className="crops">
          {[{ e:"🌴",n:"Oil Palm",y:"Perennial · 25+ year harvest" },{ e:"🍫",n:"Cocoa",y:"High-value export crop" },{ e:"🍌",n:"Plantain",y:"Fast-yield · 9 months" },{ e:"🌿",n:"Mixed Crops",y:"Diversified income" }].map(p => (
            <div key={p.n} className="cc"><div className="cc-e">{p.e}</div><div className="cc-n">{p.n}</div><div className="cc-y">{p.y}</div></div>
          ))}
        </div>
        <div style={{ marginTop:56 }}>
          <h3 style={{ fontFamily:"var(--serif)",fontSize:24,fontWeight:700,color:"var(--forest)",marginBottom:6 }}>How It Works</h3>
          <p style={{ fontSize:14,color:"#5a7060" }}>Four simple steps from investor to farm owner</p>
        </div>
        <div className="steps">
          {[
            { n:"01",t:"Choose Your Plot",d:"Select your preferred land size and location from our verified agro-estate portfolio." },
            { n:"02",t:"Pick Your Crop",d:"Choose from palm, cocoa, plantain or a mixed crop portfolio based on your return goals." },
            { n:"03",t:"We Handle Everything",d:"Our agri-experts plant, tend, fertilise, and harvest — you just own and earn." },
            { n:"04",t:"Earn for Life",d:"Receive your harvest income every season for the life of the crop — your land keeps working." },
          ].map(s => (
            <div key={s.n} className="step">
              <div className="step-n">{s.n}</div>
              <div className="step-t">{s.t}</div>
              <div className="step-d">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CATTLE */}
      <section className="sec" style={{ background:"var(--forest)" }} id="cattle-beef">
        <span className="lbl">Doxapearl Cattle & Beef</span>
        <h2 className="h2 h2l">Invest in a Cow.<br />Earn in 12 Months.</h2>
        <div className="cattle-grid">
          <div>
            <div className="c-img-wrap">
              <Photo src={IMG.cattleHero} alt="Doxapearl cattle farm" className="c-img" icon="🐄" />
              <div className="c-cap"><strong>Naturally Raised, Expertly Frozen</strong><p>Pure beef, raised with care and frozen to preserve everything that matters.</p></div>
            </div>
            <div style={{ marginTop:20 }}>
              <div className="del-t">Delivery Coverage</div>
              <div className="del"><div className="del-icon">🥶</div><div><div className="del-n">Fresh Beef — Abuja & Environs</div><div className="del-d">Same-day or next-day frozen-fresh delivery. Shop DO-12 & 14, Dawaki Modern Market.</div></div></div>
              <div className="del"><div className="del-icon">📦</div><div><div className="del-n">Dried Meat — Nationwide</div><div className="del-d">Premium dried/processed beef shipped across all of Nigeria. Order via WhatsApp or call.</div></div></div>
            </div>
          </div>
          <div>
            <p className="sub subl" style={{ marginBottom:28 }}>Own cattle without the stress. We raise, manage, and sell — you earn from the growth and sale at the end of the 12-month cycle.</p>
            <div className="cattle-plans">
              {[
                { t:"Cattle Wealth Plan",s:"Individual Cow Ownership",d:"Invest in at least one cow. We handle all rearing, feeding, and veterinary care. Earn from the growth and sale after 12 months." },
                { t:"Cow Share Program",s:"Invest Monthly · Earn Collectively",d:"Join our shared cattle plan — pool ownership with other investors and benefit from collective profits every cycle." },
                { t:"Freshlock Partnership",s:"Become a Distribution Partner",d:"Partner with us to distribute or retail Freshlock Premium Frozen Beef and earn from a fast-growing national market." },
              ].map(p => (
                <div key={p.t} className="cp"><div className="cp-t">{p.t}</div><div className="cp-s">{p.s}</div><div className="cp-d">{p.d}</div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BEEF PRODUCTS */}
      <div className="beef-sec">
        <span className="lbl">Freshlock Premium Beef</span>
        <h2 className="h2 h2l">All the Right Cuts.</h2>
        <p className="sub subl" style={{ marginTop:8 }}>Nine carefully selected beef cuts. No additives. No shortcuts. Just clean, frozen-fresh beef you can trust — every time.</p>
        <div className="beef-grid">
          <Photo src={IMG.beefProducts} alt="Freshlock beef product lineup" className="beef-main-img" icon="🥩" />
          <div>
            <Photo src={IMG.beefLifestyle} alt="Family enjoying Doxapearl beef" style={{ width:"100%",height:192,objectFit:"cover",borderRadius:"var(--rl)",marginBottom:14,display:"block" }} icon="👨‍👩‍👧" />
            <div className="beef-items">
              {[{ i:"🥩",n:"Cow Head",s:"5kg pack" },{ i:"🍖",n:"Assorted",s:"3kg pack" },{ i:"🥓",n:"Fat",s:"2kg pack" },{ i:"🍗",n:"Beefie",s:"500g starter" },{ i:"🦴",n:"Biscuit Bone",s:"Cook-ready" },{ i:"🥩",n:"Torse / Akwara",s:"Chef's choice" }].map(p => (
                <div key={p.n} className="bi"><div className="bi-i">{p.i}</div><div className="bi-n">{p.n}</div><div className="bi-s">{p.s}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CATALYST */}
      <section className="sec cat-sec" id="clyst">
        <div className="cat-glow" />
        <div style={{ position:"relative",zIndex:1 }}>
          <span className="lbl">Catalyst Token</span>
          <h2 className="h2 h2l">The Fuel of the<br />Agrovest Ecosystem.</h2>
          <div className="cat-grid">
            <div className="tok">
              <div className="tok-logo">⚗</div>
              <div className="tok-n">CATALYST</div>
              <div className="tok-t">$CLYST · BEP-20 · Binance Smart Chain</div>
              <div className="tok-d">Catalyst ($CLYST) is the native utility token of the Agrovest ecosystem — bridging agriculture, real estate, live assets and decentralised finance. Powers payments, governance, and access across all Agrovest products.</div>
              <div className="tok-q">"Be the catalyst for Growth — invest in the earth, grow your future."</div>
              <div className="tok-stats">
                {[{ v:"240M",l:"Total Supply" },{ v:"3% / 3%",l:"Buy / Sell Tax" },{ v:"90%",l:"Liquidity Pool" },{ v:"Fair",l:"Launch Type" }].map(s => (
                  <div key={s.l} className="ts"><div className="ts-v">{s.v}</div><div className="ts-l">{s.l}</div></div>
                ))}
              </div>
            </div>
            <div className="cat-feats">
              <div style={{ fontFamily:"var(--serif)",fontSize:20,fontWeight:600,color:"var(--cream)",marginBottom:14 }}>Agrovest App — 6 Products</div>
              {[{ i:"🌾",t:"Farmvest",d:"Invest in agro real estate and live assets — land, farms, livestock, standing crops" },{ i:"🛒",t:"Marketplace",d:"Buy and sell agro-products and byproducts — pay and earn in $CLYST" },{ i:"🤖",t:"Agrovest AI",d:"AI-driven farm investment insights, crop data, and market intelligence" },{ i:"💬",t:"Agrovest Chat",d:"Investor and farmer community — access gated by $CLYST holdings" },{ i:"👛",t:"Agrovest Wallet",d:"Native in-app wallet for $CLYST — store, send, and receive" },{ i:"🤝",t:"Business Consultation",d:"Book agronomy and agro-business expert sessions — fees in $CLYST" }].map(f => (
                <div key={f.t} className="cf"><div className="cf-i">{f.i}</div><div><div className="cf-t">{f.t}</div><div className="cf-d">{f.d}</div></div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="sec" style={{ background:"var(--cream)" }} id="contact">
        <span className="lbl">Get In Touch</span>
        <h2 className="h2">Ready to Grow<br />Your Wealth?</h2>
        <div className="con-grid">
          <div>
            <p className="sub" style={{ marginBottom:32 }}>Reach out to invest, partner, order beef, or learn more about any of our divisions. Based in Abuja and operating nationwide.</p>
            <div className="ci-list">
              {[{ icon:"📞",lbl:"Call / WhatsApp",val:"08130841879 · 08109292801" },{ icon:"✉️",lbl:"Email",val:"doxapearl@gmail.com" },{ icon:"📍",lbl:"Physical Office",val:"Shop DO-12 & 14, Dawaki Modern Market, Abuja" },{ icon:"🌐",lbl:"Social Media",val:"@doxapearlgroup (all platforms)" }].map(c => (
                <div key={c.lbl} className="ci"><div className="ci-icon">{c.icon}</div><div><div className="ci-lbl">{c.lbl}</div><div className="ci-val">{c.val}</div></div></div>
              ))}
            </div>
            <iframe title="Dawaki Modern Market Abuja" className="map-frame" src="https://maps.google.com/maps?q=Dawaki+Modern+Market+Abuja+Nigeria&output=embed" allowFullScreen loading="lazy" />
            <div className="socials">
              {["Instagram","TikTok","Facebook","X / Twitter","YouTube"].map(s => (
                <a key={s} href="https://instagram.com/doxapearlgroup" target="_blank" rel="noopener noreferrer" className="sc">@doxapearlgroup · {s}</a>
              ))}
            </div>
          </div>
          <div className="form-wrap">
            <div className="form-title">Send a Message</div>
            {fstate === "success" ? (
              <div className="f-success">
                <div style={{ fontSize:36,marginBottom:10 }}>✅</div>
                <div style={{ fontFamily:"var(--serif)",fontSize:20,color:"var(--cream)",marginBottom:6 }}>Message Received!</div>
                <div style={{ fontSize:13,color:"rgba(242,237,224,.6)" }}>Our team will reach out within 24 hours.</div>
                <button className="btn-p" style={{ marginTop:20 }} onClick={() => setFstate("idle")}>Send Another</button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={send}>
                {fstate === "error" && <div className="f-err">⚠ {ferr}</div>}
                <div className="form-row">
                  <div className="fg"><label className="fl">Full Name</label><input name="from_name" className="fi" placeholder="Your name" value={fd.name} onChange={e=>setFd({...fd,name:e.target.value})} required /></div>
                  <div className="fg"><label className="fl">Phone / WhatsApp</label><input name="from_phone" className="fi" placeholder="08XXXXXXXXX" value={fd.phone} onChange={e=>setFd({...fd,phone:e.target.value})} /></div>
                </div>
                <div className="fg"><label className="fl">Email Address</label><input name="from_email" type="email" className="fi" placeholder="your@email.com" value={fd.email} onChange={e=>setFd({...fd,email:e.target.value})} required /></div>
                <div className="fg">
                  <label className="fl">I'm Interested In</label>
                  <select name="interest" className="fi" value={fd.interest} onChange={e=>setFd({...fd,interest:e.target.value})} required>
                    <option value="">Select a division...</option>
                    <option>Agro Estate (Land Investment)</option>
                    <option>Cattle Wealth Plan</option>
                    <option>Cow Share Program</option>
                    <option>Freshlock Beef Order (Abuja)</option>
                    <option>Dried Meat Order (Nationwide)</option>
                    <option>Freshlock Distribution Partnership</option>
                    <option>Catalyst $CLYST Token</option>
                    <option>Castle & Properties</option>
                    <option>General Enquiry</option>
                  </select>
                </div>
                <div className="fg"><label className="fl">Message</label><textarea name="message" className="fi" placeholder="Tell us more about what you need..." value={fd.message} onChange={e=>setFd({...fd,message:e.target.value})} /></div>
                <button type="submit" className="btn-p" style={{ width:"100%",marginTop:6 }} disabled={fstate==="sending"}>
                  {fstate==="sending" ? "Sending..." : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="fg-grid">
          <div>
            <Logo light h={90} />
            <div className="fc-n" style={{ marginTop:10 }}>Doxapearl Group</div>
            <div className="fc-s">Agriculture · Agro-Estate · Real Estate · Blockchain. Creating Healthier Lives and Wealthier Homes.</div>
          </div>
          <div><div className="fc-t">Divisions</div>{["Agro Estate","Cattle & Beef","Catalyst $CLYST","Castle & Properties","Agrovest App"].map(l=><a key={l} href="#divisions" className="fl-link">{l}</a>)}</div>
          <div><div className="fc-t">Products</div>{["Freshlock Beef","Cow Share Program","Cattle Wealth Plan","Land Investment","Smart Homes"].map(l=><a key={l} href="#contact" className="fl-link">{l}</a>)}</div>
          <div><div className="fc-t">Connect</div>{["08130841879","08109292801","doxapearl@gmail.com","Dawaki, Abuja","@doxapearlgroup"].map(l=><a key={l} href="#contact" className="fl-link">{l}</a>)}</div>
        </div>
        <div className="f-bottom">
          <div className="f-copy">© {new Date().getFullYear()} Doxapearl Group. All rights reserved.</div>
          <div className="f-tag">...Real Estate. Agriculture. Blockchain.</div>
        </div>
      </footer>

      {/* WHATSAPP FLOAT */}
      <a href={WA} target="_blank" rel="noopener noreferrer" className="wa" aria-label="Chat on WhatsApp">
        <div className="wa-ring" />
        <svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="wa-tip">Chat with us</span>
      </a>
    </>
  );
}